import { ref } from 'vue';

class State_StopData {
    constructor(config) {
        this.dataProvider = config.db
        this.foundstops = ref([])
        this.map

        this.workingStops = ref([])

        // Spatial cache
        this._allStops = [];
        this._allRoutes = [];
        this._spatialInitialized = false;
        this._loadingSpatial = false;

        this.isAreaSelectionMode = ref(false);
        this.areaLayerGroup = L.layerGroup();

        // Interaction state for bi-directional highlighting
        this.hoveredRouteKey = ref(null); // format: "fromRef:toRef"
        this._routeLayersMap = new Map(); // key -> { polyline, startMarker, endMarker, baseColor, defaultWeight }

        this._loadingPromise = null;
    }

    async handleAreaSelection(bounds) {
        console.log('handleAreaSelection: Bounds received', bounds);
        this.isAreaSelectionMode.value = false;

        if (!this._spatialInitialized) {
            console.warn('handleAreaSelection: Cache not initialized. Attempting lazy init...');
            await this.initSpatialData();
        }

        const sw = bounds.getSouthWest();
        const ne = bounds.getNorthEast();
        const minLat = sw.lat, maxLat = ne.lat;
        const minLon = sw.lng, maxLon = ne.lng;

        const stopsInArea = this._allStops.filter(stop =>
            stop.lat >= minLat && stop.lat <= maxLat &&
            stop.lon >= minLon && stop.lon <= maxLon
        );
        const stopRefs = new Set(stopsInArea.map(s => s.reference));
        console.log(`handleAreaSelection: Found ${stopRefs.size} stops in selection rectangle.`);

        this.areaLayerGroup.clearLayers();
        this._routeLayersMap = new Map(); // Reset map on new selection

        if (this.map) {
            this.areaLayerGroup.addTo(this.map);
        }

        let routesFoundCount = 0;
        const stopUsage = {}; // stopRef -> count for stacking offsets at that stop

        // Helper to get and increment offset index for a specific stop
        const getAndIncr = (ref) => {
            const idx = stopUsage[ref] || 0;
            stopUsage[ref] = idx + 1;
            return idx;
        };

        // Iterate through all cached route documents
        for (const routeDoc of this._allRoutes) {
            const fromInArea = stopRefs.has(routeDoc.from);

            if (routeDoc.to) {
                for (const toRef in routeDoc.to) {
                    const toInArea = stopRefs.has(toRef);

                    // Show route if either start or end is in selection area
                    if (fromInArea || toInArea) {
                        const destData = routeDoc.to[toRef];
                        if (destData.routes && destData.routes.length > 0) {
                            const layer = this.buildEnhancedRouteLayer({
                                points: destData.routes[0].points,
                                fromRef: routeDoc.from,
                                toRef: toRef,
                                baseColor: '#FF9800', // Orange for area selection
                                isDashed: false,
                                fromOffset: getAndIncr(routeDoc.from),
                                toOffset: getAndIncr(toRef),
                                refs: destData.routes[0].refs || []
                            });
                            this.areaLayerGroup.addLayer(layer);
                            routesFoundCount++;
                        }
                    }
                }
            }
        }
        console.log(`handleAreaSelection: Rendered ${routesFoundCount} enhanced average routes.`);
    }

    getStopInfo(ref) {
        const stop = this._allStops.find(s => s.reference === ref);
        return stop ? `${stop.reference} ${stop.fullname}` : ref;
    }

    getStopName(ref) {
        const stop = this._allStops.find(s => s.reference === ref);
        return stop ? stop.fullname : ref;
    }

    buildEnhancedRouteLayer({ points, fromRef, toRef, baseColor, isDashed, fromOffset = 0, toOffset = 0, refs = [] }) {
        let layer = L.layerGroup();
        let polylineCoords = [];
        const routeKey = `${fromRef}:${toRef}`;

        // Configuration for highlighting
        const highlightColor = '#E91E63'; // Pinkish-red for highlighting
        const defaultWeight = 3;
        const highlightWeight = 6;

        const fromName = this.getStopName(fromRef);
        const toName = this.getStopName(toRef);

        // Precise 1-meter offset per index (to the right)
        // At 52 degrees North, 1m longitude is approx 0.000015 degrees.
        const METERS_PER_DEGREE = 0.000015;

        for (const [index, pos] of points.entries()) {
            if (pos?.lat && pos?.lon) {
                polylineCoords.push([Number(pos.lat), Number(pos.lon)]);
            }
        }

        if (polylineCoords.length < 2) return layer;

        const polyline = L.polyline(polylineCoords, {
            color: baseColor,
            weight: defaultWeight,
            dashArray: isDashed ? '5, 5' : null,
            lineJoin: 'round'
        }).addTo(layer);

        const refsStr = refs.length > 0 ? `<br/><b>Refs</b>: ${refs.slice(0, 5).join(', ')}${refs.length > 5 ? '...' : ''}` : '';

        // Tooltip for the route - showing IDs and Names
        let routeTooltip = `<b>Route</b>: [${fromRef}] ${fromName} → [${toRef}] ${toName}${refsStr}`;
        polyline.bindTooltip(routeTooltip, { sticky: true });

        // Markers shifted independently based on their local stop congestion
        const startPointRaw = polylineCoords[0];
        const endPointRaw = polylineCoords[polylineCoords.length - 1];

        const startPoint = [startPointRaw[0], startPointRaw[1] + (fromOffset * METERS_PER_DEGREE)];
        const endPoint = [endPointRaw[0], endPointRaw[1] + (toOffset * METERS_PER_DEGREE)];

        // Visual zones (40m and 20m) - rendered lazily on hover to save performance
        let zonesLayer = null;

        // Enhanced start tooltip with destination info
        const startTooltip = `<b>Start</b>: [${fromRef}] ${fromName}<br/><b>Dest</b>: [${toRef}] ${toName}${refsStr}`;
        const startMarker = L.circleMarker(startPoint, {
            radius: 8,
            color: 'black',
            fillColor: '#4CAF50', // Green for start
            fillOpacity: 1,
            weight: 1
        }).addTo(layer).bindTooltip(startTooltip);

        // Enhanced end tooltip with origin info
        const endTooltip = `<b>End</b>: [${toRef}] ${toName}<br/><b>Origin</b>: [${fromRef}] ${fromName}${refsStr}`;
        const endMarker = L.circleMarker(endPoint, {
            radius: 8,
            color: 'black',
            fillColor: '#F44336', // Red for end
            fillOpacity: 1,
            weight: 1
        }).addTo(layer).bindTooltip(endTooltip);

        // Store references for external highlighting
        const layerMeta = { polyline, startMarker, endMarker, baseColor, defaultWeight };
        this._routeLayersMap.set(routeKey, layerMeta);

        // Highlighting logic
        const highlight = () => {
            this.hoveredRouteKey.value = routeKey;
            polyline.setStyle({ color: highlightColor, weight: highlightWeight });
            polyline.bringToFront();

            // Lazy render zones on hover
            if (!zonesLayer) {
                zonesLayer = L.layerGroup();
                const zoneOptions = { stroke: false, fillOpacity: 0.1, interactive: false };
                L.circle(startPointRaw, { ...zoneOptions, color: '#4CAF50', radius: 40 }).addTo(zonesLayer);
                L.circle(startPointRaw, { ...zoneOptions, color: '#4CAF50', radius: 20 }).addTo(zonesLayer);
                L.circle(endPointRaw, { ...zoneOptions, color: '#F44336', radius: 40 }).addTo(zonesLayer);
                L.circle(endPointRaw, { ...zoneOptions, color: '#F44336', radius: 20 }).addTo(zonesLayer);
            }
            zonesLayer.addTo(layer);
        };

        const reset = () => {
            this.hoveredRouteKey.value = null;
            polyline.setStyle({ color: baseColor, weight: defaultWeight });
            if (zonesLayer) {
                layer.removeLayer(zonesLayer);
            }
        };

        const handleClick = () => {
            console.log(`Map Click: Adding stop ${fromRef} to panel`);
            this.AddStop(fromRef);
        };

        [polyline, startMarker, endMarker].forEach(item => {
            item.on('mouseover', highlight);
            item.on('mouseout', reset);
            item.on('click', handleClick);
        });

        return layer;
    }

    // External control for bi-directional highlighting
    setExternalHighlight(fromRef, toRef) {
        const key = `${fromRef}:${toRef}`;
        const meta = this._routeLayersMap.get(key);
        if (meta) {
            meta.polyline.setStyle({ color: '#E91E63', weight: 6 });
            meta.polyline.bringToFront();
        }
    }

    clearExternalHighlight(fromRef, toRef) {
        const key = `${fromRef}:${toRef}`;
        const meta = this._routeLayersMap.get(key);
        if (meta) {
            meta.polyline.setStyle({ color: meta.baseColor, weight: meta.defaultWeight });
        }
    }

    async displayAverageRoute(routeData) {
        // Obsolete, logic moved to handleAreaSelection and buildEnhancedRouteLayer
    }

    buildAverageRouteLayer(routeData) {
        // Obsolete, replaced by buildEnhancedRouteLayer
        return L.layerGroup();
    }

    async initSpatialData() {
        const currentDb = this.dataProvider.value.dbname;

        // If we are already loading THIS database, wait for it.
        if (this._loadingSpatial === currentDb && this._loadingPromise) {
            console.log(`initSpatialData: Already loading [${currentDb}], waiting for in-progress load.`);
            return this._loadingPromise;
        }

        console.log(`initSpatialData: START - Fetching bulk data for [${currentDb}]...`);
        this._loadingSpatial = currentDb;
        this._spatialInitialized = false;

        // Clear existing cache to prevent stale data usage
        this._allStops = [];
        this._allRoutes = [];

        this._loadingPromise = (async () => {
            try {
                const [stops, routes] = await Promise.all([
                    this.dataProvider.value.fetchAllStops(),
                    this.dataProvider.value.fetchAllRoutes()
                ]);

                // Validate that we are still on the same database after async calls
                if (this.dataProvider.value.dbname !== currentDb) {
                    console.warn(`initSpatialData: Database changed during load ([${currentDb}] -> [${this.dataProvider.value.dbname}]). Discarding results.`);
                    return;
                }

                // Pre-parse coordinates for faster filtering
                this._allStops = stops.map(s => ({
                    ...s,
                    lat: Number(s.lat),
                    lon: Number(s.lon)
                }));
                this._allRoutes = routes;
                this._spatialInitialized = true;
                console.log(`initSpatialData: DONE - Loaded ${stops.length} stops and ${routes.length} routes for [${currentDb}].`);
            } catch (error) {
                console.error(`initSpatialData: ERROR during bulk fetch for [${currentDb}]:`, error);
                throw error;
            } finally {
                if (this._loadingSpatial === currentDb) {
                    this._loadingSpatial = false;
                    this._loadingPromise = null;
                }
            }
        })();

        return this._loadingPromise;
    }

    findRoutesInArea(bounds) {
        // This method is now effectively part of handleAreaSelection to be more efficient
        // with the hierarchical data structure
        return [];
    }

    async findStops(matchString) {
        console.log('findStops in')
        this.foundstops.value = await this.dataProvider.value.findStops(matchString);
        console.log('findStops out')
    }

    async fetchStopToStopRawRoute(guid, fromstop, tostop) {

        const ref = fromstop + "_" + tostop
        console.log(`fetchStopToStopRawRoute guid:${guid}`)
        console.log(`fetchStopToStopRawRoute ref :${ref}`)
        let rawroute = await this.dataProvider.value.fetchStopToStopRawRoute(guid, fromstop, tostop);

        return rawroute;
    }

    async clearStops() {
        this.foundstops.value = [];
    }

    setMap(mapInstance) {
        this.map = mapInstance;
    }

    addLayer(layer) {
        console.log("addLayer - IN")
        if (layer && this.map) {
            layer.addTo(this.map);
        } else {
            console.log("addLayer - we have no map")
        }
    }

    removeLayer(layer) {
        if (layer && this.map) {
            this.map.removeLayer(layer);
        }
    }

    async AddStop(ref) {
        console.log(`Add stop ${ref} to state`)
        // Check if already in working stops
        if (this.workingStops.value.find(s => s.reference === ref)) {
            console.log(`AddStop: ${ref} already exists`);
            return;
        }

        const details = await this.dataProvider.value.GetStopDetails(ref)
        const dbname = this.dataProvider.value.dbname

        this.workingStops.value.push({
            reference: ref,
            details: details,
            dbname: dbname
        })
        console.log(`AddStop ${ref} added to list`)
    }

    RemoveStop(index) {
        this.workingStops.value.splice(index, 1);
    }

    MoveStopUp(index) {
        if (index > 0) {
            const item = this.workingStops.value.splice(index, 1)[0];
            this.workingStops.value.splice(index - 1, 0, item);
        }
    }

    MoveStopDown(index) {
        if (index < this.workingStops.value.length - 1) {
            const item = this.workingStops.value.splice(index, 1)[0];
            this.workingStops.value.splice(index + 1, 0, item);
        }
    }

}

export default State_StopData;